/**
 * Owner: Masa Bayu
 * File: text-plate-detector.ts
 * Date:Time: 2026-10-05 13:45
 * Description: Fasa 17 · detect image text-plates that duplicate live text.
 */

import type { FlatAtom } from './types';

export type TextPlateDetection = {
  readonly textPlates: ReadonlyArray<{
    readonly imageId: string;
    readonly textId: string;
    readonly overlap: number;
  }>;
  readonly suppressImageIds: readonly string[];
};

const OVERLAP_IMG_THRESHOLD = 0.7;
const SIZE_SIMILARITY = 0.8;
/** Text almost fully inside a compact image strip (p18 Raster39 path). */
const TEXT_CONTAINED = 0.9;
const STRIP_WIDTH_RATIO = 0.75;
const STRIP_MAX_HEIGHT_FACTOR = 2.5;
const STRIP_MAX_WIDTH_FACTOR = 1.5;

/**
 * Semantic visual dedupe · suppress image atoms that are decorative text plates
 * of a live text atom (geometry overlap), not mandalas / cream / figures.
 */
export function detectTextPlates(atoms: readonly FlatAtom[]): TextPlateDetection {
  const textPlates: Array<{ imageId: string; textId: string; overlap: number }> = [];
  const suppressImageIds: string[] = [];

  const images = atoms.filter((a) => a.type === 'image');
  const texts = atoms.filter((a) => a.type === 'text');

  for (const img of images) {
    for (const txt of texts) {
      const overlapImg = calculateOverlap(img, txt);
      if (isTextPlatePair(img, txt, overlapImg)) {
        textPlates.push({
          imageId: img.id,
          textId: txt.id,
          overlap: overlapImg,
        });
        suppressImageIds.push(img.id);
        break;
      }
    }
  }

  return { textPlates, suppressImageIds };
}

function isTextPlatePair(img: FlatAtom, txt: FlatAtom, overlapImg: number): boolean {
  // Path A · Owner: image mostly covered by co-located text box + similar size.
  if (overlapImg >= OVERLAP_IMG_THRESHOLD && isSimilarSize(img, txt, SIZE_SIMILARITY)) {
    return true;
  }

  // Path B · text-plate strip: live text contained in a compact image band.
  const overlapTxt = calculateTextCoverage(img, txt);
  if (overlapTxt < TEXT_CONTAINED) return false;
  const widthRatio =
    Math.min(img.width, txt.width) / Math.max(img.width, txt.width || 1);
  if (widthRatio < STRIP_WIDTH_RATIO) return false;
  if (img.height > txt.height * STRIP_MAX_HEIGHT_FACTOR) return false;
  if (img.width > txt.width * STRIP_MAX_WIDTH_FACTOR) return false;
  return true;
}

function calculateOverlap(a: FlatAtom, b: FlatAtom): number {
  const xOverlap = Math.max(
    0,
    Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x),
  );
  const yOverlap = Math.max(
    0,
    Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y),
  );
  const overlapArea = xOverlap * yOverlap;
  const imgArea = Math.max(1e-6, a.width * a.height);
  return overlapArea / imgArea;
}

function calculateTextCoverage(img: FlatAtom, txt: FlatAtom): number {
  const xOverlap = Math.max(
    0,
    Math.min(img.x + img.width, txt.x + txt.width) - Math.max(img.x, txt.x),
  );
  const yOverlap = Math.max(
    0,
    Math.min(img.y + img.height, txt.y + txt.height) - Math.max(img.y, txt.y),
  );
  const overlapArea = xOverlap * yOverlap;
  const textArea = Math.max(1e-6, txt.width * txt.height);
  return overlapArea / textArea;
}

function isSimilarSize(a: FlatAtom, b: FlatAtom, threshold: number): boolean {
  const widthRatio =
    Math.min(a.width, b.width) / Math.max(a.width, b.width || 1);
  const heightRatio =
    Math.min(a.height, b.height) / Math.max(a.height, b.height || 1);
  return widthRatio >= threshold && heightRatio >= threshold;
}
